import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authLists, formatListRef } from '@/api/authLists';
import type { AuthListKind, AuthListRow } from '@/api/authLists';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Trash2, Pencil, List, Ban, ShieldCheck, Hash } from 'lucide-react';
import { useCustomerContext, useEffectiveCustomerId } from '@/lib/customer-context-store';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { AuthListPhoneSearch } from './AuthListPhoneSearch';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

export function AuthListsPage() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const canMutate = CAN_MUTATE.includes(user.role);

  const [kindFilter, setKindFilter] = useState<'all' | AuthListKind>('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AuthListRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AuthListRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['authLists', kindFilter, effectiveCustomerId],
    queryFn: () =>
      authLists.list({
        ...(kindFilter !== 'all' ? { kind: kindFilter } : {}),
        customer_id: effectiveCustomerId ?? undefined,
      }),
  });

  const rows = useMemo(() => data?.rows ?? [], [data]);

  // The detail page's "Edit list" button navigates back here with
  // location.state.editId set so we open the EditListDialog automatically.
  // Avoids duplicating the metadata-edit form on the detail page.
  useEffect(() => {
    const editId = (location.state as { editId?: string } | null)?.editId;
    if (!editId || !rows.length) return;
    const row = rows.find((r) => r.id === editId);
    if (row) {
      setEditTarget(row);
      // Clear the state so the dialog doesn't re-open on a tab visit.
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, rows, navigate]);

  const remove = useMutation({
    mutationFn: (id: string) => authLists.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['authLists'] });
      setDeleteTarget(null);
    },
  });

  const columns = [
    {
      header: 'Ref',
      className: 'w-20',
      cell: (r: AuthListRow) => (
        <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
          {formatListRef(r.ref_number)}
        </span>
      ),
    },
    {
      header: 'Name',
      cell: (r: AuthListRow) => (
        // Link uses the human-friendly AL-N form so URLs stay readable when
        // shared. The backend's getAuthList accepts both UUID and AL-N.
        <Link
          to={`/auth-lists/${formatListRef(r.ref_number)}`}
          className="text-sm font-medium text-foreground hover:underline underline-offset-2"
        >
          {r.name}
        </Link>
      ),
    },
    {
      header: 'Kind',
      cell: (r: AuthListRow) => <KindBadge kind={r.kind} />,
    },
    {
      header: 'Entries',
      cell: (r: AuthListRow) => (
        <span className="font-mono text-xs tabular-nums text-slate-600 dark:text-slate-300">
          {r.entry_count}
        </span>
      ),
    },
    {
      header: 'Description',
      cell: (r: AuthListRow) =>
        r.description
          ? (
            <span className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">
              {r.description}
            </span>
          )
          : <span className="text-xs text-slate-300 dark:text-slate-600">—</span>,
    },
    {
      header: 'Updated',
      cell: (r: AuthListRow) => (
        <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">
          {new Date(r.updated_at).toLocaleString()}
        </span>
      ),
    },
    {
      header: '',
      className: 'w-10 text-end',
      cell: (r: AuthListRow) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Row actions"
              onClick={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={() => navigate(`/auth-lists/${formatListRef(r.ref_number)}`)}>
              <List className="h-3.5 w-3.5 me-2" />
              Manage entries
            </DropdownMenuItem>
            {canMutate && (
              <DropdownMenuItem onClick={() => setEditTarget(r)}>
                <Pencil className="h-3.5 w-3.5 me-2" />
                Edit
              </DropdownMenuItem>
            )}
            {canMutate && (
              <DropdownMenuItem
                className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/40"
                onClick={() => setDeleteTarget(r)}
              >
                <Trash2 className="h-3.5 w-3.5 me-2" />
                Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Access control"
        title="Auth lists"
        description="Simple named blacklists and allowlists applied per IVR node. Matches callers by phone number or PIN — distinct from the caller-profile-based auth modes."
        actions={
          <div className="flex items-center gap-2">
            <Select
              value={kindFilter}
              onValueChange={(v) => setKindFilter(v as typeof kindFilter)}
            >
              <SelectTrigger className="h-9 w-40 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kinds</SelectItem>
                <SelectItem value="allowlist">Allow lists (CID)</SelectItem>
                <SelectItem value="blacklist">Block lists (CID)</SelectItem>
                <SelectItem value="pin_list">PIN lists</SelectItem>
                <SelectItem value="cid_pin_list">CID + PIN lists</SelectItem>
              </SelectContent>
            </Select>
            {canMutate && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 me-1.5" />
                New list
              </Button>
            )}
          </div>
        }
      />

      <AuthListPhoneSearch />

      {isLoading ? (
        <div className="text-sm text-slate-400 dark:text-slate-500">Loading…</div>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          empty={
            kindFilter !== 'all'
              ? `No ${kindFilter}s yet. Create one to start gating IVR nodes.`
              : 'No auth lists yet. Create a blacklist to block specific callers, or an allowlist to restrict access to a set of phone numbers / PINs.'
          }
        />
      )}

      {createOpen && (
        <CreateListDialog onClose={() => setCreateOpen(false)} />
      )}
      {editTarget && (
        <EditListDialog list={editTarget} onClose={() => setEditTarget(null)} />
      )}
      {deleteTarget && (
        <Dialog open onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete auth list?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              <span className="font-medium">{deleteTarget.name}</span>{' '}
              ({deleteTarget.kind}) will be soft-deleted. Any IVR nodes
              currently referencing this list will have their auth-list
              reference cleared — node behaviour reverts to unfiltered.
            </p>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={remove.isPending}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                variant="destructive"
                disabled={remove.isPending}
                onClick={() => remove.mutate(deleteTarget.id)}
              >
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Entry-management has graduated to a real page at /auth-lists/:id —
          rows + the "Manage entries" menu item now navigate there. The old
          AuthListDetailDialog component is kept around for reference but is
          no longer mounted here. */}
    </>
  );
}

// ── Kind badge ──────────────────────────────────────────────────────────────

export function KindBadge({ kind }: { kind: AuthListKind }) {
  if (kind === 'blacklist') {
    return (
      <Badge variant="outline" className="gap-1 border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
        <Ban className="h-3 w-3" />
        Block list (CID)
      </Badge>
    );
  }
  if (kind === 'pin_list') {
    return (
      <Badge variant="outline" className="gap-1 border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/40 dark:text-blue-300">
        <Hash className="h-3 w-3" />
        PIN list
      </Badge>
    );
  }
  if (kind === 'cid_pin_list') {
    return (
      <Badge variant="outline" className="gap-1 border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-900/50 dark:bg-purple-950/40 dark:text-purple-300">
        <ShieldCheck className="h-3 w-3" />
        CID + PIN list
      </Badge>
    );
  }
  // allowlist
  return (
    <Badge variant="outline" className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-300">
      <ShieldCheck className="h-3 w-3" />
      Allow list (CID)
    </Badge>
  );
}

// ── Create dialog ───────────────────────────────────────────────────────────

function CreateListDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const current = useCustomerContext((s) => s.current);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<AuthListKind>('allowlist');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      authLists.create({
        name: name.trim(),
        kind,
        description: description.trim() || null,
        // Forward customer context so the backend can scope the new list.
        // Required for super_admin (needs both) and reseller_owner (needs
        // customer_id since their JWT carries no customerId).
        ...(current?.id ? { customer_id: current.id } : {}),
        ...(current?.resellerId ? { reseller_id: current.resellerId } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['authLists'] });
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Create failed.');
    },
  });

  const canSubmit = name.trim().length > 0;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New auth list</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <CustomerScopeChip />
          <div className="space-y-1">
            <Label htmlFor="al-name">Name</Label>
            <Input
              id="al-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Paid subscribers, Blocked spammers"
            />
          </div>
          <div className="space-y-1">
            <Label>Kind</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as AuthListKind)}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="allowlist">Allow list (CID) — allow specific phone numbers</SelectItem>
                <SelectItem value="blacklist">Block list (CID) — reject specific phone numbers</SelectItem>
                <SelectItem value="pin_list">PIN list — allow callers who know the PIN</SelectItem>
                <SelectItem value="cid_pin_list">CID + PIN list — require both phone number and PIN</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {(() => {
                const kindDescriptions: Record<AuthListKind, string> = {
                  allowlist: 'Only callers whose phone number is in the list are allowed through.',
                  blacklist: 'Callers whose phone number matches an entry are rejected immediately.',
                  pin_list: 'Callers must enter a PIN to pass — no caller ID check. Entries must include a PIN.',
                  cid_pin_list: 'Callers must match both a phone number AND a PIN. Entries must include both.',
                };
                return kindDescriptions[kind];
              })()}
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="al-desc">Description (optional)</Label>
            <Textarea
              id="al-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="What's this list for?"
            />
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={create.isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            onClick={() => create.mutate()}
            disabled={create.isPending || !canSubmit}
          >
            {create.isPending ? 'Creating…' : 'Create list'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit dialog ─────────────────────────────────────────────────────────────

function EditListDialog({
  list, onClose,
}: { list: AuthListRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(list.name);
  const [kind, setKind] = useState<AuthListKind>(list.kind);
  const [description, setDescription] = useState(list.description ?? '');
  const [error, setError] = useState<string | null>(null);

  const patch = useMutation({
    mutationFn: () =>
      authLists.update(list.id, {
        name: name.trim(),
        kind,
        description: description.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['authLists'] });
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Update failed.');
    },
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit {list.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="eal-name">Name</Label>
            <Input
              id="eal-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Kind</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as AuthListKind)}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="allowlist">Allow list (CID)</SelectItem>
                <SelectItem value="blacklist">Block list (CID)</SelectItem>
                <SelectItem value="pin_list">PIN list</SelectItem>
                <SelectItem value="cid_pin_list">CID + PIN list</SelectItem>
              </SelectContent>
            </Select>
            {kind !== list.kind && kind === 'blacklist' && (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Switching to block list requires every entry to have a phone
                number. PIN-only entries will be rejected by the save.
              </p>
            )}
            {kind !== list.kind && kind === 'cid_pin_list' && (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Switching to CID + PIN list requires every entry to have both a
                phone number and a PIN. Entries missing either will be rejected
                by the save.
              </p>
            )}
            {kind !== list.kind && kind === 'pin_list' && (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Switching to PIN list requires every entry to have a PIN.
                Entries without a PIN will be rejected by the save.
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="eal-desc">Description</Label>
            <Textarea
              id="eal-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={patch.isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            onClick={() => patch.mutate()}
            disabled={patch.isPending || !name.trim()}
          >
            {patch.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
